import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, DataTable, EmptyState, PageHeader, Tag } from '@/design/components'
import type { Column } from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { useBreadcrumb } from '@/app/Breadcrumb'
import { EMPTY, formatDate } from '@/lib/format'
import { EditTimeEntryModal } from '../components/EditTimeEntryModal'
import { NewTimeEntryModal } from '../components/NewTimeEntryModal'
import { formatHoursDecimal, formatMinutes } from '../format'
import { useTimeEntries, useTimesheetStats } from '../hooks'
import type { TimeEntry } from '../types'

const PAGE_SIZE = 50

/** Ekran „Czas pracy" — moje wpisy + podsumowanie miesiąca (KPI z `/timesheet/stats`). */
export function TimesheetPage() {
  const { user, can } = useMe()
  useBreadcrumb(['Sprawy', 'Czas pracy'])

  const canLog = can('timesheet.log')

  const [page, setPage] = useState(1)
  const [newOpen, setNewOpen] = useState(false)
  const [prefillCaseId, setPrefillCaseId] = useState<number | null>(null)
  const [selected, setSelected] = useState<TimeEntry | null>(null)

  // Wejście z karty sprawy („Rejestruj czas" → /czas?sprawa=<id>&nowa=1) otwiera modal
  // z wybraną sprawą i czyści query param — wzorzec `?nowa=1` z pulpitu → rejestr spraw.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('nowa') !== '1') return
    const caseParam = Number(searchParams.get('sprawa'))
    const next = new URLSearchParams(searchParams)
    next.delete('nowa')
    next.delete('sprawa')
    setSearchParams(next, { replace: true })
    if (can('timesheet.log')) {
      setPrefillCaseId(Number.isInteger(caseParam) && caseParam > 0 ? caseParam : null)
      setNewOpen(true)
    }
  }, [searchParams, setSearchParams, can])

  const stats = useTimesheetStats()
  // „Moje wpisy": lista jest jawnie zawężona do zalogowanego — nawet rola widząca
  // wszystkich (Partner) ma tu swój timesheet, nie cudzy.
  // Wejście z karty sprawy przez kliknięcie WIERSZA (`/czas?sprawa=<id>`, bez
  // `nowa=1`) zawęża listę do tej sprawy — inaczej klik lądował na pełnej liście.
  const caseFilter = (() => {
    if (searchParams.get('nowa') === '1') return null
    const raw = Number(searchParams.get('sprawa'))
    return Number.isInteger(raw) && raw > 0 ? raw : null
  })()

  const listing = useTimeEntries({
    page,
    size: PAGE_SIZE,
    // Wejście z karty sprawy pokazuje czas CAŁEJ sprawy, nie tylko swój — inaczej
    // klik w cudzy wpis lądował na pustej liście („Brak wpisów czasu"), mimo że
    // zakładka sprawy pokazywała ich dziesięć. Zakres i tak egzekwuje backend.
    user_id: caseFilter ? undefined : user.id,
    case_id: caseFilter ?? undefined,
  })

  const clearCaseFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('sprawa')
    setSearchParams(next, { replace: true })
    setPage(1)
  }
  const caseFilterLabel = listing.data?.items.find((e) => e.case)?.case?.signature

  const subtitle = stats.data
    ? `W tym miesiącu: ${formatHoursDecimal(stats.data.month_minutes)} h łącznie · ` +
      `${formatHoursDecimal(stats.data.month_billable_minutes)} h rozliczalne · ` +
      `dziś ${formatMinutes(stats.data.today_minutes || 0)}`
    : 'Wczytywanie podsumowania miesiąca…'

  const columns: Column<TimeEntry>[] = [
    {
      key: 'work_date',
      header: 'Data',
      width: '110px',
      render: (row) => <span className="whitespace-nowrap text-ink">{formatDate(row.work_date)}</span>,
    },
    {
      key: 'case',
      header: 'Sprawa',
      width: '230px',
      render: (row) =>
        row.case ? (
          <div className="min-w-0 leading-tight">
            <p className="t-mono truncate text-ink">{row.case.signature}</p>
            <p className="mt-0.5 truncate text-small text-muted">{row.case.client_name}</p>
          </div>
        ) : (
          EMPTY
        ),
    },
    {
      key: 'description',
      header: 'Opis',
      render: (row) =>
        row.description ? (
          <span className="text-ink">{row.description}</span>
        ) : (
          <span className="text-muted">{EMPTY}</span>
        ),
    },
    {
      key: 'billable',
      header: 'Rozliczalny',
      width: '120px',
      render: (row) =>
        row.billable ? <Tag active>rozliczalny</Tag> : <Tag>wewnętrzny</Tag>,
    },
    {
      key: 'minutes',
      header: 'Czas',
      width: '110px',
      align: 'right',
      render: (row) => <span className="t-mono whitespace-nowrap text-ink">{formatMinutes(row.minutes)}</span>,
    },
  ]

  return (
    <>
      <PageHeader
        title="Czas pracy"
        subtitle={subtitle}
        actions={
          canLog ? (
            <Button variant="primary" onClick={() => { setPrefillCaseId(null); setNewOpen(true) }}>
              Rejestruj czas
            </Button>
          ) : null
        }
      />

      {caseFilter ? (
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-2 rounded border border-line bg-card px-2.5 py-1 text-small text-ink">
            Sprawa: <span className="t-mono">{caseFilterLabel ?? `#${caseFilter}`}</span>
            <button
              type="button"
              aria-label="Wyczyść zawężenie do sprawy"
              onClick={clearCaseFilter}
              className="text-muted transition-colors hover:text-ink"
            >
              ✕
            </button>
          </span>
        </div>
      ) : null}

      <DataTable<TimeEntry>
        columns={columns}
        rows={listing.data?.items ?? []}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelected(row)}
        total={listing.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={listing.isPending}
        empty={
          <EmptyState
            title="Brak wpisów czasu"
            description="Zarejestruj pierwszą czynność — czas trafi na zestawienie sprawy."
            action={
              canLog ? (
                <Button variant="primary" onClick={() => { setPrefillCaseId(null); setNewOpen(true) }}>
                  Rejestruj czas
                </Button>
              ) : null
            }
          />
        }
      />

      <NewTimeEntryModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        initialCaseId={prefillCaseId}
      />
      <EditTimeEntryModal entry={selected} canEdit={canLog} onClose={() => setSelected(null)} />
    </>
  )
}
